import { useAuth } from '../context/AuthContext';

/**
 * Custom hook that provides common auth state checks
 * This eliminates duplication of auth state logic across components
 */
export function useAuthState() {
  const auth = useAuth();
  
  // Simple, stable loading check: user === null OR profile loading for authenticated users
  const isLoading = auth.user === null || (auth.user && typeof auth.user === 'object' && auth.profile === null);
  const isAuthenticated = auth.user && typeof auth.user === 'object';
  const isUnauthenticated = auth.user === false;
  
  // Profile-dependent states - wait for profile to be loaded
  const profileLoading = isAuthenticated && auth.profile === null;
  const hasCompletedOnboarding = auth.profile?.onboarding_completed;
  const hasActiveSubscription = auth.hasActiveSubscription;
  const isEmailConfirmed = auth.isEmailConfirmed;
  
  // Common redirect conditions - don't make decisions while profile is loading
  const needsEmailConfirmation = isAuthenticated && !isEmailConfirmed;
  const needsOnboarding = isAuthenticated && isEmailConfirmed && !profileLoading && !hasCompletedOnboarding;
  const needsSubscription = isAuthenticated && isEmailConfirmed && !profileLoading && hasCompletedOnboarding && !hasActiveSubscription;
  const canAccessDashboard = isAuthenticated && isEmailConfirmed && !profileLoading && hasCompletedOnboarding && hasActiveSubscription;
  
  return {
    // Original auth object
    ...auth,
    
    // Computed states - these now override any from auth context for consistency
    isLoading,
    isAuthenticated,
    isUnauthenticated,
    profileLoading,
    hasCompletedOnboarding,
    hasActiveSubscription,
    isEmailConfirmed,
    
    // Redirect conditions
    needsEmailConfirmation,
    needsOnboarding,
    needsSubscription,
    canAccessDashboard,
  };
}

/**
 * Hook specifically for auth redirects
 * Returns the appropriate redirect path based on user state
 */
export function useAuthRedirect() {
  const {
    isLoading,
    isUnauthenticated,
    needsEmailConfirmation,
    needsOnboarding,
    needsSubscription,
    canAccessDashboard
  } = useAuthState();
  
  if (isLoading) return null;
  if (isUnauthenticated) return '/signin';
  if (needsEmailConfirmation) return '/email-confirmation';
  if (needsOnboarding) return '/onboarding';
  if (needsSubscription) return '/payment';
  if (canAccessDashboard) return '/dashboard';
  
  return null;
} 